import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { TRACKS, WEIGHTS, STAGES, fmtDate, fmtDateTime, scoreColor } from "@/lib/constants";
import { TrackBadge } from "@/components/TrackBadge";
import { ScoreRing, ScoreBar } from "@/components/ScoreBar";
import { ProvenancePill } from "@/components/Provenance";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { VoiceDictate } from "@/components/VoiceDictate";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  ArrowLeft, Sparkles, Mail, FileText, Check, X, Sliders, ExternalLink,
  AlertTriangle, RotateCcw, History, Bell, Lightbulb, ShieldAlert, SendHorizontal,
} from "lucide-react";
export default function OpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: opp, isLoading } = useQuery({ queryKey: ["opp", id], queryFn: async () => (await api.get(`/opportunities/${id}`)).data });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["opp", id] }); qc.invalidateQueries({ queryKey: ["opps"] }); };

  const action = useMutation({
    mutationFn: async ({ url, body }) => (await api.post(url, body || {})).data,
    onSuccess: refresh,
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (isLoading || !opp) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

  const scores = opp.scores || {};
  const outreachDrafts = (opp.drafts || []).filter((d) => d.kind === "outreach");
  const submissionDrafts = (opp.drafts || []).filter((d) => d.kind === "submission");

  return (
    <div className="flex h-full">
      <div className="flex-1 overflow-y-auto p-6 lg:p-8 max-w-4xl mx-auto w-full space-y-6">
        <button onClick={() => navigate("/triage")} data-testid="back-to-triage" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to triage
        </button>

        <header className="space-y-3">
          <div className="flex items-start gap-4">
            <ScoreRing score={scores.total || 0} size={64} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <TrackBadge track={opp.track} />
                {opp.suppressed && <Badge variant="outline" className="border-rose-500/40 bg-rose-500/10 text-rose-400 gap-1"><ShieldAlert className="h-3 w-3" /> suppressed</Badge>}
                {opp.is_duplicate && <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-400 gap-1"><AlertTriangle className="h-3 w-3" /> duplicate</Badge>}
                {opp.ai_suggested && <Badge variant="outline" className="border-sky-500/40 bg-sky-500/10 text-sky-400 gap-1"><Sparkles className="h-3 w-3" /> AI-suggested · verify</Badge>}
              </div>
              <h1 className="font-heading text-2xl font-bold tracking-tight mt-2">{opp.title}</h1>
              <p className="text-muted-foreground">{opp.org_name} · {opp.location || "—"}</p>
            </div>
          </div>
          <ProvenancePill provenance={{ source: opp.source, source_url: opp.source_url, scraped_at: opp.scraped_at, confidence: opp.track_confidence }} aiGenerated={opp.ai_scored} />
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" onClick={() => action.mutate({ url: `/opportunities/${id}/promote` })} data-testid="promote-btn" disabled={opp.status === "promoted"} className="gap-1.5"><Check className="h-4 w-4" /> Promote</Button>
            <RejectButton id={id} onDone={refresh} />
            <Button size="sm" variant="outline" onClick={() => action.mutate({ url: `/opportunities/${id}/classify` })} data-testid="rescore-btn" className="gap-1.5"><RotateCcwWrap /> Re-score</Button>
            {opp.event_url && <Button size="sm" variant="outline" asChild><a href={opp.event_url} target="_blank" rel="noreferrer" className="gap-1.5"><ExternalLink className="h-4 w-4" /> Source</a></Button>}
            <div className="ml-auto">
              <Select value={opp.pipeline_stage} onValueChange={(v) => action.mutate({ url: `/opportunities/${id}`, body: { pipeline_stage: v } })}>
                <SelectTrigger className="w-[150px]" data-testid="stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {opp.is_duplicate && (
            <div className="flex items-center justify-between rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
              <span className="text-amber-300">Flagged as a possible duplicate by org/domain or event URL.</span>
              <Button size="sm" variant="outline" onClick={() => action.mutate({ url: `/opportunities/${id}/dedupe-override` })} data-testid="dedupe-override-btn">Mark unique</Button>
            </div>
          )}
        </header>

        {/* Why matched + scoring */}
        <Card className="p-5 border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold">Fit scoring & why matched</h2>
            <ScoreOverrideDialog id={id} current={scores.total || 0} onDone={refresh} />
          </div>
          {opp.manual_override && (
            <div className="mb-4 rounded-lg border border-primary/30 bg-primary/10 p-3 text-sm">
              <span className="font-medium text-primary">Manual override to {opp.manual_override.total}.</span>{" "}
              <span className="text-muted-foreground">{opp.manual_override.reason} — by {opp.manual_override.by}</span>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {["relevance", "budget", "confidence", "logistics"].map((k) => (
              <div key={k}>
                <ScoreBar label={k} value={scores[k] || 0} weight={WEIGHTS[k]} />
                <p className="text-xs text-muted-foreground mt-1.5">{opp.score_reasons?.[k]}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-border">
            <div className="text-xs text-muted-foreground mb-2">Classification evidence · {opp.track_confidence}% confidence</div>
            <div className="flex flex-wrap gap-2">
              {(opp.classification_reasons || []).map((r, i) => (
                <span key={i} className="text-xs rounded-full border border-border bg-muted/50 px-2.5 py-1">{r}</span>
              ))}
            </div>
          </div>
          {opp.next_best_action && (
            <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-primary/20 bg-primary/5 p-3">
              <Lightbulb className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <div><div className="text-xs font-medium text-primary">Recommended next action</div><p className="text-sm text-muted-foreground mt-0.5">{opp.next_best_action}</p></div>
            </div>
          )}
        </Card>

        {/* Drafts */}
        <Card className="p-5 border-border">
          <h2 className="font-heading text-lg font-semibold mb-4">Drafting & handoff</h2>
          <Tabs defaultValue="outreach">
            <TabsList data-testid="draft-tabs">
              <TabsTrigger value="outreach" data-testid="tab-outreach" className="gap-1.5"><Mail className="h-4 w-4" /> Outreach</TabsTrigger>
              <TabsTrigger value="submission" data-testid="tab-submission" className="gap-1.5"><FileText className="h-4 w-4" /> Submission</TabsTrigger>
            </TabsList>
            <TabsContent value="outreach" className="pt-4">
              <DraftPanel oppId={id} kind="outreach" drafts={outreachDrafts} onDone={refresh} />
            </TabsContent>
            <TabsContent value="submission" className="pt-4">
              <DraftPanel oppId={id} kind="submission" drafts={submissionDrafts} onDone={refresh} />
            </TabsContent>
          </Tabs>
        </Card>
      </div>

      {/* Right evidence/audit rail */}
      <aside className="w-[340px] shrink-0 border-l border-border overflow-y-auto p-5 hidden xl:block bg-secondary/30">
        <ReminderBlock oppId={id} reminders={opp.reminders || []} onDone={refresh} />
        <div className="mt-6">
          <h3 className="flex items-center gap-2 font-heading font-semibold text-sm mb-3"><History className="h-4 w-4" /> Audit trail</h3>
          <ol className="relative border-l border-border ml-1.5 space-y-4">
            {(opp.activity || []).map((a) => (
              <li key={a.id} className="ml-4" data-testid={`audit-${a.id}`}>
                <span className="absolute -left-1.5 h-3 w-3 rounded-full bg-primary/40 border-2 border-background" />
                <div className="text-xs font-medium capitalize">{a.action.replace(/_/g, " ")}</div>
                <div className="text-[11px] text-muted-foreground">{a.detail}</div>
                <div className="text-[10px] text-muted-foreground/70 mt-0.5">{fmtDateTime(a.timestamp)} · {a.actor}</div>
              </li>
            ))}
          </ol>
        </div>
      </aside>
    </div>
  );
}

function RotateCcwWrap() { return <RotateCcw className="h-4 w-4" />; }

function RejectButton({ id, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="reject-opportunity-btn" className="gap-1.5 text-rose-400 border-rose-500/30 hover:bg-rose-500/10"><X className="h-4 w-4" /> Reject</Button>
      <ConfirmDialog open={open} onOpenChange={setOpen} destructive testid="reject-confirm"
        title="Reject this opportunity?" description="It will be archived and removed from active triage."
        ackLabel="I confirm this opportunity is not a fit and should be archived."
        confirmLabel="Reject & archive"
        onConfirm={async () => { await api.post(`/opportunities/${id}/reject`, {}); toast.success("Rejected"); onDone(); }} />
    </>
  );
}

function ScoreOverrideDialog({ id, current, onDone }) {
  const [open, setOpen] = useState(false);
  const [total, setTotal] = useState(current);
  const [reason, setReason] = useState("");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button size="sm" variant="outline" onClick={() => { setTotal(current); setOpen(true); }} data-testid="score-override-button" className="gap-1.5"><Sliders className="h-4 w-4" /> Override</Button>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Manual score override</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>New total score (0–100)</Label><Input type="number" min={0} max={100} value={total} onChange={(e) => setTotal(Number(e.target.value))} data-testid="override-score-input" /></div>
          <div className="space-y-1.5"><Label>Reason (logged to audit trail) *</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="override-reason-input" /></div>
        </div>
        <DialogFooter>
          <Button disabled={!reason} data-testid="override-submit-btn" onClick={async () => {
            await api.post(`/opportunities/${id}/score-override`, { total, reason }); toast.success("Override saved"); setOpen(false); onDone();
          }}>Save override</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DraftPanel({ oppId, kind, drafts, onDone }) {
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState({});
  const [showDiff, setShowDiff] = useState({});
  const [confirm, setConfirm] = useState(null);

  const generate = async () => {
    setGenerating(true);
    try { await api.post(`/opportunities/${oppId}/drafts/generate?kind=${kind}`); toast.success("Draft generated"); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const saveEdit = async (d) => {
    await api.patch(`/drafts/${d.id}`, { edited_content: editing[d.id] });
    toast.success("Draft saved"); setEditing((p) => ({ ...p, [d.id]: undefined })); onDone();
  };

  return (
    <div className="space-y-4">
      <Button onClick={generate} disabled={generating} data-testid={`generate-${kind}-btn`} className="gap-2">
        <Sparkles className="h-4 w-4" /> {generating ? "Drafting with Claude…" : `Generate ${kind} draft`}
      </Button>

      {drafts.map((d) => {
        const isEditing = editing[d.id] !== undefined;
        const content = isEditing ? editing[d.id] : d.edited_content;
        const edited = d.edited_content !== d.ai_content;
        return (
          <Card key={d.id} className="p-4 border-border" data-testid={`draft-${d.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
              <div className="flex items-center gap-2">
                <StatusBadge status={d.status} />
                {edited && <button onClick={() => setShowDiff((p) => ({ ...p, [d.id]: !p[d.id] }))} data-testid="ai-draft-diff-toggle" className="text-xs text-primary hover:underline">{showDiff[d.id] ? "Hide" : "Show"} diff vs AI</button>}
              </div>
              <ProvenancePill provenance={d.provenance} aiGenerated={d.ai_generated} />
            </div>
            {d.model_used && d.model_used !== "template" && (
              <div className="text-[11px] text-muted-foreground mb-2">Drafted by <span className="font-mono text-primary">{d.model_used}</span></div>
            )}
            {d.subject && <div className="text-sm mb-2"><span className="text-muted-foreground">Subject: </span><span className="font-medium">{d.subject}</span></div>}

            {showDiff[d.id] && edited && (
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <div className="rounded-lg border border-border bg-muted/40 p-3">
                  <div className="text-[11px] text-muted-foreground mb-1.5">Original AI draft</div>
                  <pre className="text-xs whitespace-pre-wrap font-sans text-muted-foreground">{d.ai_content}</pre>
                </div>
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                  <div className="text-[11px] text-primary mb-1.5">Your edited version</div>
                  <pre className="text-xs whitespace-pre-wrap font-sans">{d.edited_content}</pre>
                </div>
              </div>
            )}

            {isEditing ? (
              <>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-xs text-muted-foreground">Edit by typing, or dictate to append text at the end.</span>
                  <VoiceDictate testid={`dictate-${d.id}`} onText={(t) => setEditing((p) => {
                    const cur = p[d.id] || "";
                    const sep = cur && !/\s$/.test(cur) ? " " : "";
                    return { ...p, [d.id]: cur + sep + t };
                  })} />
                </div>
                <Textarea value={content} onChange={(e) => setEditing((p) => ({ ...p, [d.id]: e.target.value }))} rows={10} data-testid={`draft-edit-${d.id}`} className="font-sans text-sm" />
              </>
            ) : (
              <pre className="text-sm whitespace-pre-wrap font-sans rounded-lg bg-muted/30 p-3 max-h-64 overflow-y-auto">{content}</pre>
            )}

            <div className="flex items-center gap-2 flex-wrap mt-3">
              {isEditing ? (
                <>
                  <Button size="sm" onClick={() => saveEdit(d)} data-testid={`save-edit-${d.id}`}>Save edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing((p) => ({ ...p, [d.id]: undefined }))}>Cancel</Button>
                </>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setEditing((p) => ({ ...p, [d.id]: d.edited_content }))} data-testid={`edit-draft-${d.id}`}>Edit</Button>
              )}
              {d.status === "draft" && !isEditing && (
                <Button size="sm" onClick={async () => { await api.post(`/drafts/${d.id}/submit-for-approval`); toast.success("Queued for approval"); onDone(); }} data-testid={`submit-approval-${d.id}`}>Send to approval queue</Button>
              )}
              {kind === "submission" && (d.status === "approved" || d.status === "submitted") && (
                <FormHandoff draftId={d.id} status={d.status} onDone={onDone} />
              )}
              {kind === "outreach" && d.status === "approved" && (
                <Button size="sm" onClick={() => setConfirm(d)} data-testid={`send-outreach-${d.id}`} className="gap-1.5"><SendHorizontal className="h-4 w-4" /> Send email</Button>
              )}
              {d.status === "sent" && <span className="text-xs text-emerald-400">Email sent ✓</span>}
            </div>
          </Card>
        );
      })}
      {drafts.length === 0 && <p className="text-sm text-muted-foreground">No {kind} drafts yet.</p>}

      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} testid="send-confirm"
        title="Send this outreach email?" description={`This emails the organization's primary contact. There is no autonomous send — this is your explicit, final action.`}
        ackLabel="I have reviewed the content and approve sending this email now."
        confirmLabel="Send email now"
        onConfirm={async () => {
          try { await api.post(`/drafts/${confirm.id}/send`, { confirm: true }); toast.success("Outreach email sent"); setConfirm(null); onDone(); }
          catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        }} />
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft: "border-border bg-muted/50 text-muted-foreground",
    pending_approval: "border-amber-500/30 bg-amber-500/10 text-amber-400",
    approved: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
    rejected: "border-rose-500/30 bg-rose-500/10 text-rose-400",
    sent: "border-primary/30 bg-primary/10 text-primary",
    submitted: "border-primary/30 bg-primary/10 text-primary",
  };
  return <Badge variant="outline" className={map[status] || map.draft}>{status.replace(/_/g, " ")}</Badge>;
}

function FormHandoff({ draftId, status, onDone }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const { data } = useQuery({ queryKey: ["mapping", draftId], queryFn: async () => (await api.get(`/drafts/${draftId}/form-mapping`)).data, enabled: open });

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`form-handoff-${draftId}`} className="gap-1.5"><ExternalLink className="h-4 w-4" /> Assisted form handoff</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Assisted browser handoff</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Copy each mapped answer into the submission form, then mark complete. The app never auto-submits.</p>
          {data?.event_url && <Button variant="outline" size="sm" asChild className="gap-1.5"><a href={data.event_url} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" /> Open submission form</a></Button>}
          <div className="space-y-2.5">
            {(data?.fields || []).map((f, i) => (
              <div key={i} className="rounded-lg border border-border p-3" data-testid={`handoff-field-${i}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-medium">{f.label} <span className="text-muted-foreground opacity-70">· {f.source}</span></span>
                  <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => { navigator.clipboard.writeText(f.value); toast.success(`Copied ${f.label}`); }} data-testid={`copy-field-${i}`}>Copy</Button>
                </div>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{f.value || "—"}</p>
              </div>
            ))}
          </div>
          <DialogFooter>
            {status === "approved"
              ? <Button onClick={() => { setOpen(false); setConfirm(true); }} data-testid="handoff-complete-btn">Mark submission complete</Button>
              : <span className="text-sm text-emerald-400">Submission completed ✓</span>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} testid="handoff-confirm"
        title="Mark this submission complete?" description="Confirm you have personally reviewed and submitted the form in the browser."
        ackLabel="I completed and submitted this form myself in the organization's portal."
        confirmLabel="Mark complete"
        onConfirm={async () => { await api.post(`/drafts/${draftId}/handoff-complete`, { confirm: true }); toast.success("Submission marked complete"); onDone(); }} />
    </>
  );
}

function ReminderBlock({ oppId, reminders, onDone }) {
  const [due, setDue] = useState("");
  const [note, setNote] = useState("");
  const add = async () => {
    if (!due || !note) return;
    await api.post("/reminders", { opportunity_id: oppId, due_date: due, note });
    toast.success("Reminder set"); setDue(""); setNote(""); onDone();
  };
  return (
    <div>
      <h3 className="flex items-center gap-2 font-heading font-semibold text-sm mb-3"><Bell className="h-4 w-4" /> Follow-up reminders</h3>
      <div className="space-y-2 mb-3">
        {reminders.map((r) => (
          <div key={r.id} className="flex items-center gap-2 rounded-lg border border-border p-2 text-xs" data-testid={`reminder-${r.id}`}>
            <input type="checkbox" checked={!!r.done} onChange={async () => { await api.patch(`/reminders/${r.id}`, { done: !r.done }); onDone(); }} className="accent-primary" />
            <div className={`flex-1 ${r.done ? "line-through opacity-60" : ""}`}><div>{r.note}</div><div className="text-muted-foreground">{fmtDate(r.due_date)}</div></div>
          </div>
        ))}
        {reminders.length === 0 && <p className="text-xs text-muted-foreground">No reminders.</p>}
      </div>
      <div className="space-y-2">
        <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="reminder-date" className="h-9 text-xs" />
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Follow-up note…" data-testid="reminder-note" className="h-9 text-xs" />
        <Button size="sm" variant="outline" onClick={add} data-testid="add-reminder-btn" className="w-full">Add reminder</Button>
      </div>
    </div>
  );
}
